import type { NavSettings, Permission } from '../types'

type HasPermission = (permission: Permission) => boolean

interface NavChild {
	path: string
	label: string
	icon: string
	permission?: Permission
	activePath?: string
}

// Trap: nav entries point at the final path, never at a redirect. A bare <Navigate>
// route renders without the Layout for one frame, which blanks the whole page
// before the real route mounts.

type NavGroup = 'bar' | 'team' | 'guests' | 'admin'

export interface NavItem {
	path: string
	label: string
	icon: string
	permission: Permission | null
	group?: NavGroup
	children?: NavChild[]
}

const NAV_GROUPS: { id: NavGroup; label: string }[] = [
	{ id: 'bar', label: 'Bar' },
	{ id: 'team', label: 'Team' },
	{ id: 'guests', label: 'Guests' },
	{ id: 'admin', label: 'Admin' },
]

export const ALL_NAV_ITEMS: NavItem[] = [
	{ path: '/', label: 'Home', icon: 'ti-home', permission: null },
	{ path: '/shift', label: 'Shift', icon: 'ti-clock-play', permission: null },
	{ path: '/tasks', label: 'Tasks', icon: 'ti-checklist', permission: null },
	{ path: '/operations/on-tap', label: 'On tap', icon: 'ti-beer', permission: 'view_keg_recipes', group: 'bar' },
	{ path: '/operations/recipes', label: 'Recipes', icon: 'ti-flask', permission: 'view_prep_recipes', group: 'bar' },
	{ path: '/operations/quality', label: 'Quality', icon: 'ti-checkup-list', permission: 'view_keg_recipes', group: 'bar' },
	{ path: '/operations/shopping', label: 'Shopping list', icon: 'ti-shopping-cart', permission: 'view_keg_recipes', group: 'bar' },
	{ path: '/operations/checklists', label: 'Checklists', icon: 'ti-list-check', permission: null, group: 'bar' },
	{
		path: '/schedule',
		label: 'Schedule',
		icon: 'ti-calendar-time',
		permission: null,
		group: 'team',
		children: [
			{ path: '/schedule', label: 'Week', icon: 'ti-calendar-time' },
			{ path: '/schedule/availability', label: 'My availability', icon: 'ti-calendar-user' },
			{ path: '/schedule/timesheets', label: 'Timesheets', icon: 'ti-clock-check', permission: 'manage_schedule' },
		],
	},
	{
		path: '/handbook',
		label: 'Handbook',
		icon: 'ti-book',
		permission: null,
		group: 'team',
		children: [
			{ path: '/handbook', label: 'Overview', icon: 'ti-book' },
			{ path: '/handbook/proposals', label: 'Proposals', icon: 'ti-bulb', permission: 'manage_documents' },
		],
	},
	{
		path: '/staff',
		label: 'People',
		icon: 'ti-users',
		permission: null,
		group: 'team',
		children: [
			{ path: '/staff/team', label: 'Directory', icon: 'ti-address-book' },
			{ path: '/staff/hiring', label: 'Hiring', icon: 'ti-user-search', permission: 'manage_hiring' },
			{ path: '/staff/users', label: 'Users', icon: 'ti-user', permission: 'manage_users' },
		],
	},
	{ path: '/leads', label: 'Events', icon: 'ti-calendar-event', permission: 'manage_leads', group: 'guests' },
	{ path: '/vouchers', label: 'Vouchers', icon: 'ti-ticket', permission: 'manage_loyalty', group: 'guests' },
	{
		path: '/finance',
		label: 'Finance',
		icon: 'ti-chart-bar',
		permission: null,
		group: 'admin',
		children: [
			{ path: '/finance/sales', label: 'Sales', icon: 'ti-receipt', permission: 'manage_finance' },
			{ path: '/finance', label: 'Overview', icon: 'ti-chart-bar', permission: 'manage_finance' },
			{ path: '/finance/events', label: 'Event P&L', icon: 'ti-report-money', permission: 'manage_finance' },
			{
				path: '/finance/transactions/bank',
				label: 'Transactions',
				icon: 'ti-arrows-exchange',
				permission: 'manage_finance',
				activePath: '/finance/transactions',
			},
			{ path: '/finance/invoices', label: 'Invoices', icon: 'ti-file-invoice', permission: 'view_invoices' },
		],
	},
]

export const SETTINGS_NAV_ITEM: NavItem = {
	path: '/settings',
	label: 'Settings',
	icon: 'ti-settings',
	permission: 'manage_settings',
}

const BOTTOM_NAV_PATHS = ['/', '/shift', '/tasks', '/finance', '/operations/recipes', '/schedule', '/operations/on-tap', '/handbook']

export const BOTTOM_NAV_SLOTS = 4

export const HOME_PATH = '/'

export const DEFAULT_NAV_SETTINGS: NavSettings = { ordered_paths: [], hidden_paths: [], bottom_paths: [] }

export function isPathActive(pathname: string, path: string): boolean {
	if (path === '/') return pathname === '/'
	return pathname === path || pathname.startsWith(`${path}/`)
}

export function visibleChildren(item: NavItem, hasPermission: HasPermission): NavChild[] {
	return (item.children ?? []).filter((child) => child.permission === undefined || hasPermission(child.permission))
}

function isNavItemVisible(item: NavItem, hasPermission: HasPermission): boolean {
	if (item.permission !== null && !hasPermission(item.permission)) return false
	return item.children === undefined || visibleChildren(item, hasPermission).length > 0
}

export function entryPath(item: NavItem, hasPermission: HasPermission): string {
	return visibleChildren(item, hasPermission)[0]?.path ?? item.path
}

export function hasChildMenu(item: NavItem, hasPermission: HasPermission): boolean {
	return visibleChildren(item, hasPermission).length > 1
}

export function isChildActive(pathname: string, parent: NavItem, child: NavChild): boolean {
	if (child.path === parent.path) return pathname === child.path
	return isPathActive(pathname, child.activePath ?? child.path)
}

export function visibleNavItems(hasPermission: HasPermission): NavItem[] {
	return ALL_NAV_ITEMS.filter((item) => isNavItemVisible(item, hasPermission))
}

function inPreferredOrder(items: NavItem[], orderedPaths: readonly string[]): NavItem[] {
	const rank = (item: NavItem) => {
		const index = orderedPaths.indexOf(item.path)
		return index === -1 ? orderedPaths.length : index
	}
	return [...items].sort((a, b) => rank(a) - rank(b))
}

export function isHidden(item: NavItem, settings: NavSettings): boolean {
	return item.path !== HOME_PATH && settings.hidden_paths.includes(item.path)
}

export function hiddenNavItems(hasPermission: HasPermission, settings: NavSettings): NavItem[] {
	return inPreferredOrder(visibleNavItems(hasPermission), settings.ordered_paths).filter((item) => isHidden(item, settings))
}

export function bottomNavItems(hasPermission: HasPermission, settings: NavSettings): NavItem[] {
	const items = visibleNavItems(hasPermission)
	const itemsAt = (paths: readonly string[]) =>
		paths.flatMap((path) => items.filter((item) => item.path === path)).slice(0, BOTTOM_NAV_SLOTS)
	const chosen = itemsAt(settings.bottom_paths)
	return chosen.length > 0 ? chosen : itemsAt(BOTTOM_NAV_PATHS)
}

export function activeSectionWithChildren(pathname: string, hasPermission: HasPermission): NavItem | null {
	return visibleNavItems(hasPermission).find((item) => isPathActive(pathname, item.path) && hasChildMenu(item, hasPermission)) ?? null
}

interface NavSection {
	label: string | null
	items: NavItem[]
}

export function navSections(hasPermission: HasPermission, settings: NavSettings = DEFAULT_NAV_SETTINGS): NavSection[] {
	const items = inPreferredOrder(visibleNavItems(hasPermission), settings.ordered_paths).filter((item) => !isHidden(item, settings))
	const ungrouped: NavSection = { label: null, items: items.filter((item) => item.group === undefined) }
	const grouped = NAV_GROUPS.map((group) => ({
		label: group.label,
		items: items.filter((item) => item.group === group.id),
	}))
	return [ungrouped, ...grouped].filter((section) => section.items.length > 0)
}

export const PROFILE_PATH = '/profile'

export const NAV_SETTINGS_PATH = '/profile/navigation'

export function navTitle(pathname: string, hasPermission: HasPermission): string {
	if (pathname === PROFILE_PATH) return 'My profile'
	if (pathname === NAV_SETTINGS_PATH) return 'Navigation'
	const items = [...visibleNavItems(hasPermission), SETTINGS_NAV_ITEM]
	const parent = items.find((item) => isPathActive(pathname, item.path))
	if (!parent) return 'SAYSAY'
	const child = visibleChildren(parent, hasPermission).find((candidate) => isChildActive(pathname, parent, candidate))
	if (!child || child.path === parent.path) return parent.label
	return child.label
}
